import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import authStore from './authStore';
import routing from './routingStore';
import viewStore from './viewStore';
import Http from '../Http';
import Storage from '../Storage';
import utils, { parseError } from '../utils';

const STORAGE_KEY = 'omavero';
const VEROTILIT_STORAGE_KEY = 'verotilit';

class OmaveroStore {
  @observable skeleton;
  @observable currentId;

  @observable index = new Map();
  @observable loadingIndex = new Map();
  @observable loadingIndexBackground = new Map();

  @observable items = new Map();
  @observable loadingItems = new Map();

  @observable verotilit;
  @observable selectedVerotili;

  @observable selectedErapvm;

  // last sync times
  indexSynced = {};
  itemsSynced = {};

  canGoBack = true;


  constructor() {
    makeObservable(this);
    const items = Storage.get(STORAGE_KEY);

    if (items) {
      this.items.replace(items);
    }

    const verotilit = Storage.get(VEROTILIT_STORAGE_KEY);

    if (verotilit) {
      this.verotilit = verotilit;
      this.selectedVerotili = verotilit[0].VEROTILIID;
    }
  }

  @computed
  get cacheId() {
    return authStore.companyToken + '.' + this.currentId;
  }

  @computed
  get hasModule() {
    return authStore.hasModule(12);
  }

  @computed
  get isLoading() {
    return this.loadingItems.has(this.currentId);
  }
  @computed
  get isLoadingIndex() {
    return this.loadingIndex.has(authStore.companyToken);
  }

  @computed
  get list() {
    return this.index.get(authStore.companyToken);
  }

  @computed
  get item() {
    return this.items.get(this.cacheId) || this.skeleton || {};
  }

  @computed
  get erapvm() {
    if (!this.item.KOHDEVUOSI) return undefined;

    if (this.selectedErapvm) return this.selectedErapvm;

    // Date's month is zerobased
    // default +1 month
    let month = this.item.KOHDEKAUSI;

    let dateObj, date;

    // yearly
    if (!month) {
      // last of feb
      dateObj = new Date(this.item.KOHDEVUOSI, 2, 0);
      date = dateObj.getDate();
    } else {
      // no tas, 2 months
      if (!this.item.tas) {
        month++;
      }

      date = 12;
      dateObj = new Date(this.item.KOHDEVUOSI, month, date);
    }

    // skip the weekend
    const day = dateObj.getDay();

    if (day === 0) {
      date += 1;
    }
    if (day === 6) {
      date += 2;
    }

    dateObj.setDate(date);

    return dateObj;
  }

  @computed
  get verottajaIban() {
    if (!this.verotilit) {
      return undefined;
    }
    return this.verotilit.find(item => item.VEROTILIID === this.selectedVerotili).IBAN;
  }

  @action.bound
  clear() {
    this.skeleton = null;
    this.currentcurrentId = null;
    this.itemsSynced = {};
    this.items.clear();
  }

  @action.bound
  reset() {

  }

  @action.bound
  async fetchIndex() {
    if (!this.hasModule) {
      return;
    }

    const companyToken = authStore.companyToken;

    if (!this.index.has(companyToken)) {
      this.loadingIndex.set(companyToken, true);
    } else {
      if (this.indexSynced[companyToken] && this.indexSynced[companyToken] > Date.now() - utils.CACHE_TIME) {
        return;
      }

      this.loadingIndexBackground.set(companyToken, true);
    }

    await this.updateIndex();

    runInAction(() => {
      this.loadingIndex.delete(companyToken);
      this.loadingIndexBackground.delete(companyToken);
    });
  }

  @action.bound
  async refreshIndex() {
    const companyToken = authStore.companyToken;

    if (this.loadingIndex.has(companyToken)) {
      return;
    }

    this.loadingIndex.set(companyToken, true);

    await this.updateIndex();

    runInAction(() => {
      this.loadingIndex.delete(companyToken);
    });
  }

  @action.bound
  async updateIndex() {
    const companyToken = authStore.companyToken;

    this.indexError = false;

    try {
      const omaverot = await Http.crequest('omavero');

      runInAction(() => {
        this.index.set(companyToken, omaverot);
      });

      Storage.set('omaveroindex', this.index);

      this.indexSynced[companyToken] = Date.now();
    } catch (error) {
      viewStore.showSnackbar(parseError(error));
      runInAction(() => {
        this.indexError = true;
      });
    }
  }

  @action.bound
  async fetch() {
    const currentId = this.currentId;

    if (this.items.has(this.cacheId) && this.itemsSynced[currentId] && this.itemsSynced[currentId] > Date.now() - utils.CACHE_TIME) {
      return;
    }

    this.loadingItems.set(currentId, true);

    await this.update();

    runInAction(() => {
      this.loadingItems.delete(currentId);
    });
  }

  @action.bound
  async refresh() {
    const currentId = this.currentId;

    if (this.loadingItems.has(currentId)) {
      return;
    }

    this.loadingItems.set(currentId, true);

    await this.update();

    runInAction(() => {
      this.loadingItems.delete(currentId);
    });
  }

  @action.bound
  async update() {
    const currentId = this.currentId;
    const cacheId = this.cacheId;

    this.getVerotilit();

    try {
      const item = await Http.crequest('omavero/' + currentId);

      const arvot = {};
      item.arvot.forEach(row => arvot[row.TUNNUS] = row);
      item.arvot = arvot;

      runInAction(() => {
        this.items.set(cacheId, item);

        if (item.OLETUSIBANVEROTTAJA) {
          this.selectedVerotili = item.OLETUSIBANVEROTTAJA;
        }
      });

      Storage.set(STORAGE_KEY, this.items);

      this.itemsSynced[currentId] = Date.now();
    } catch (error) {
      viewStore.showSnackbar(parseError(error));
    }
  }

  @action.bound
  async getVerotilit() {

    if (this.verotilit) {
      return;
    }
    try {
      const verotilit = await Http.crequest('verotilit');

      runInAction(() => {
        this.verotilit = verotilit;
        this.selectedVerotili = verotilit[0].VEROTILIID;
      });

      Storage.set(VEROTILIT_STORAGE_KEY, this.verotilit);
    } catch (error) {
      viewStore.showSnackbar('Verotilien haku epäonnistui');
    }
  }

  @action.bound
  selectVerotili(e) {
    this.selectedVerotili = e.target.value;
  }

  @action.bound
  show(data) {
    // this.saveScroll();

    this.skeleton = data;

    this.canGoBack = true;

    routing.push(`/omavero/${data.KAUSIVEROILMOITUSID}`);
  }

  @action.bound
  hide() {
    this.currentId = null;

    if (this.canGoBack) {
      routing.goBack();
    } else {
      routing.push('/omavero');
    }
  }

  @action.bound
  setCurrent(id, state) {
    if (state) {
      this.skeleton = state.skeleton;
    }

    this.selectedErapvm = null;

    this.currentId = id;
    this.fetch();
  }

  @action.bound
  setErapvm(value) {
    this.selectedErapvm = value;
  }
}

export default new OmaveroStore();
